import { createSlice } from "@reduxjs/toolkit";
import {
  getPnlByYear,
  getPnlByYearFiltered,
  getTickerPerformance,
  getTagPerformance,
  getEmotionalPerformance,
  getLeaderboard,
  getLeaderboardPage,
  getSetupTypePerformance,
  getPlanAdherence,
  getReviewCoverage,
  getRiskRewardStats,
  getJournalFrequency,
  getJournalStreak,
  getJournalCoverage,
  getAccountValueMetrics,
  getOptionsExpiryPerformance,
  getTagCoOccurrence,
  getTagPerformanceTimeseries,
} from "./thunks";
import { logout, expiredLogout, deleteUser } from "../auth/thunks";
import {
  addFulfilledCases,
  setField,
  setFieldFromPayloadKey,
} from "../utils/asyncHandlers";

const initialState = {
  leaderboard: [],
  tickerPerformance: [],
  tagPerformance: [],
  emotionalPerformance: [],
  pnlYTD: {},
  performanceCard: {},
  // TRADE-951 Insights
  setupTypePerformance: [],
  planAdherence: {},
  reviewCoverage: {},
  riskReward: {},
  journalFrequency: [],
  journalStreak: {},
  journalCoverage: {},
  accountValueMetrics: {},
  optionsExpiry: [],
  tagCoOccurrence: [],
  tagTimeseries: {},
};

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    addFulfilledCases(builder, [getPnlByYear, getPnlByYearFiltered], setField("pnlYTD"));
    addFulfilledCases(builder, [getLeaderboard, getLeaderboardPage], setField("leaderboard"));
    builder
      .addCase(
        getTickerPerformance.fulfilled,
        setFieldFromPayloadKey("tickerPerformance", "ticker_performance", [])
      )
      .addCase(
        getTagPerformance.fulfilled,
        setFieldFromPayloadKey("tagPerformance", "tag_performance", [])
      )
      .addCase(
        getEmotionalPerformance.fulfilled,
        setFieldFromPayloadKey("emotionalPerformance", "emotional_performance", [])
      )
      .addCase(
        getSetupTypePerformance.fulfilled,
        setFieldFromPayloadKey("setupTypePerformance", "setup_type_performance", [])
      )
      .addCase(
        getPlanAdherence.fulfilled,
        setFieldFromPayloadKey("planAdherence", "plan_adherence", {})
      )
      .addCase(
        getReviewCoverage.fulfilled,
        setFieldFromPayloadKey("reviewCoverage", "review_coverage", {})
      )
      .addCase(
        getRiskRewardStats.fulfilled,
        setFieldFromPayloadKey("riskReward", "risk_reward", {})
      )
      .addCase(
        getJournalFrequency.fulfilled,
        setFieldFromPayloadKey("journalFrequency", "journal_frequency", [])
      )
      .addCase(
        getJournalStreak.fulfilled,
        setFieldFromPayloadKey("journalStreak", "journal_streak", {})
      )
      .addCase(
        getJournalCoverage.fulfilled,
        setFieldFromPayloadKey("journalCoverage", "journal_coverage", {})
      )
      .addCase(
        getAccountValueMetrics.fulfilled,
        setFieldFromPayloadKey("accountValueMetrics", "account_value_metrics", {})
      )
      .addCase(
        getOptionsExpiryPerformance.fulfilled,
        setFieldFromPayloadKey("optionsExpiry", "options_expiry_performance", [])
      )
      .addCase(
        getTagCoOccurrence.fulfilled,
        setFieldFromPayloadKey("tagCoOccurrence", "tag_co_occurrence", [])
      )
      .addCase(
        getTagPerformanceTimeseries.fulfilled,
        setFieldFromPayloadKey("tagTimeseries", "tag_timeseries", {})
      )
      .addCase(deleteUser.fulfilled, (state) => {
        state.pnlYTD = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.pnlYTD = null;
        state.performanceCard = {};
        state.setupTypePerformance = [];
        state.planAdherence = {};
        state.reviewCoverage = {};
        state.riskReward = {};
        state.journalFrequency = [];
        state.journalStreak = {};
        state.journalCoverage = {};
        state.accountValueMetrics = {};
        state.optionsExpiry = [];
        state.tagCoOccurrence = [];
        state.tagTimeseries = {};
      })
      .addCase(expiredLogout.fulfilled, (state) => {
        state.pnlYTD = null;
      });
  },
});

export default analyticsSlice.reducer;
